import { FC, lazy } from 'react'
import { LmsSentry, LtiCallbackPage, OIDCLoginCallback } from 'react-common'
import { Navigate, useRoutes } from 'react-router-dom'
import { CustomSuspense } from '@/components/CustomSuspense/CustomSuspense'
import { ErrorHandler } from '@/components/ErrorHandler/ErrorHandler'
import { useIsLearnerDashboardEnabled } from '@/features/dashboard/useIsLearnerDashboardEnabled'
import { isNewLearnerMF } from '@/features/learnerMF/isNewLearnerMF'
import { CourseDetailsPrefetcher } from '@/views/courses/CourseDetailsPrefetcher'
import { CourseParent } from '@/views/courses/CourseParent'
import { CourseRedirectByActivityId } from '@/views/courses/CourseRedirectByActivityId/CourseRedirectByActivityId'
import { CourseUnavailable } from '@/views/courses/CourseUnavailable/CourseUnavailable'
import { KMEmailResponse } from '@/views/KMEmailResponse/KMEmailResponse'
import { NotFoundPage } from '@/views/NotFoundPage'
import { ProfilePage } from '@/views/Profile/Profile'
import { AuthHandleLogin } from './components/AuthHandleLogin/AuthHandleLogin'
import { PublicLayoutMain } from './features/layout/PublicLayoutMain/PublicLayoutMain'
import Forbidden from './views/Forbidden/Forbidden'
import { Home } from './views/Home'

const CourseDetails = lazy(() => import('@/views/courses/CourseDetails/CourseDetails'))
const CourseDetailsMF = lazy(() => import('@/views/courses/CourseDetails/CourseDetailsMF'))
const CourseView = lazy(() => import('@/views/courses/CourseView'))
const Catalog = lazy(() => import('@/views/Catalog/Catalog'))
const IssuedCertificatePreview = lazy(() => import('@/views/IssuedCertificatePreview/IssuedCertificatePreview'))
const PublicCatalogRouter = lazy(() => import('./views/PublicCatalogRouter/PublicCatalogRouter'))
const LearnerDashboard = lazy(() => import('./views/LearnerDashboard/LearnerDashboard'))
const LearnerCalendar = lazy(() => import('./views/LearnerCalendar/LearnerCalendar'))

export const courseViewBaseUrl = '/courses/:objectiveId/view/'
export const catalogDetailPath = '/catalog/:objectiveId/*'

const NavigateToCoursesOrShowDashboard = () => {
  const isDashboardEnabled = useIsLearnerDashboardEnabled()
  if (isDashboardEnabled)
    return (
      <CustomSuspense>
        <LearnerDashboard />
      </CustomSuspense>
    )

  const urlToRedirect = '/courses'
  const urlSearchParams = new URLSearchParams(window.location.search).toString()
  const urlHash = window.location.hash
  const fullUrl = `${urlToRedirect}${urlSearchParams ? `?${urlSearchParams}` : ''}${urlHash || ''}`
  return <Navigate to={fullUrl} />
}

const SimulateError = () => {
  throw new Error('test errr Learner react')
}

export const ROUTES = [
  {
    element: <AuthHandleLogin />,
    children: [
      { path: '/', element: <NavigateToCoursesOrShowDashboard /> },
      { path: '/courses', element: <Home /> },
      {
        path: '/catalog',
        element: (
          <CustomSuspense>
            <Catalog />
          </CustomSuspense>
        ),
      },
      {
        element: <ErrorHandler component={CourseParent} errorComponent={CourseUnavailable} />,
        children: [
          {
            path: catalogDetailPath,
            element: (
              <CustomSuspense>
                <ErrorHandler component={CourseDetailsPrefetcher} errorComponent={CourseUnavailable} />
              </CustomSuspense>
            ),
          },
          {
            path: '/courses/redirect/:objectiveId',
            element: <ErrorHandler component={CourseRedirectByActivityId} errorComponent={CourseUnavailable} />,
          },
          {
            path: '/courses/:objectiveId/*',
            element: <ErrorHandler component={CourseDetailsPrefetcher} errorComponent={CourseUnavailable} />,
          },
          {
            path: '/courses/:objectiveId/certificate/:certificateNumber',
            element: (
              <CustomSuspense>
                <IssuedCertificatePreview />
              </CustomSuspense>
            ),
          },
          {
            path: `${courseViewBaseUrl}*`,
            element: (
              <CustomSuspense>
                <ErrorHandler component={CourseView} errorComponent={CourseUnavailable} />
              </CustomSuspense>
            ),
          },
          {
            path: '/courses/:objectiveId',
            element: (
              <CustomSuspense>
                <ErrorHandler
                  component={isNewLearnerMF() ? CourseDetailsMF : CourseDetails}
                  errorComponent={CourseUnavailable}
                />
              </CustomSuspense>
            ),
          },
          {
            path: '/courses/:objectiveId/workflow/:workflowId/view',
            element: (
              <CustomSuspense>
                <ErrorHandler component={CourseView} errorComponent={CourseUnavailable} />
              </CustomSuspense>
            ),
          },
        ],
      },
      {
        path: '/calendar/*',
        element: (
          <CustomSuspense>
            <LearnerCalendar />
          </CustomSuspense>
        ),
      },
      {
        path: '/email-response-callback',
        element: (
          <CustomSuspense>
            <KMEmailResponse />
          </CustomSuspense>
        ),
      },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/forbidden', element: <Forbidden /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/certificate',
    element: <PublicLayoutMain hideMenu />,
    children: [
      {
        path: ':certificateNumber',
        element: (
          <CustomSuspense>
            <IssuedCertificatePreview isPublic />
          </CustomSuspense>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/open-catalog/*',
    element: (
      <CustomSuspense>
        <PublicCatalogRouter />
      </CustomSuspense>
    ),
  },
  { path: '/lti-tool-callback', element: <LtiCallbackPage /> },
  { path: '/simulate-error', element: <SimulateError /> },
  { path: '/login-callback', element: <OIDCLoginCallback /> },
]

const sentryUseRoutes = LmsSentry.wrapUseRoutes(useRoutes)

export const Router: FC = () => {
  return sentryUseRoutes(ROUTES)
}
